import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { FolderKanban, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { cn } from '@/lib/utils'
import { useCrm2Board, useCrm2Meta, type Deal, type Pipeline } from './api'
import { DealSheet } from './DealSheet'
import { NewDealDialog } from './NewDealDialog'
import { Crm2Frame, Dot, LEVEL_SOFT, money, shortDay, usePeopleNames } from './shared'

const ORDER: Pipeline[] = ['marketing', 'sales', 'post_sales', 'partnership', 'podcast']
const LABEL: Record<Pipeline, string> = { marketing: 'Marketing', sales: 'Sales', post_sales: 'Post-sales', partnership: 'Partnership', podcast: 'Podcast' }

/** One pipeline at a time, as columns. The URL holds the pipeline, filters and the open deal. */
export function BoardPage() {
  const [params, setParams] = useSearchParams()
  const pipeline = (ORDER.includes(params.get('p') as Pipeline) ? params.get('p') : 'marketing') as Pipeline
  const closed = params.get('closed') === '1'
  const mine = params.get('owner') === 'me'
  const openId = params.get('deal')
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const board = useCrm2Board(pipeline, closed)
  const meta = useCrm2Meta()
  const me = useIdentity()
  const name = usePeopleNames()

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const keep = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (d: Deal) => (!mine || d.ownerId === me.userId) && (!needle || `${d.accountName} ${d.name}`.toLowerCase().includes(needle))
  }, [q, mine, me.userId])

  const connected = pipeline === 'marketing' || pipeline === 'sales' || pipeline === 'post_sales'

  return (
    <Crm2Frame
      title="Pipelines"
      description="Marketing hands a deal to Sales at Demo Done; a signed contract opens Post-sales. Partnership and Podcast run on their own."
      actions={
        <Button size="sm" onClick={() => setCreating(true)} disabled={!meta.data}>
          <Plus className="size-4" />New deal
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-lg border border-border bg-surface p-0.5 text-sm" role="group" aria-label="Pipeline">
          {ORDER.map((p, i) => (
            <button key={p} type="button" aria-pressed={pipeline === p} onClick={() => set('p', p === 'marketing' ? null : p)} className={cn('rounded-md px-3 py-1', pipeline === p ? 'bg-accent font-medium' : 'text-muted-foreground', i === 3 && 'ml-2 border-l border-border-soft')}>
              {LABEL[p]}
              {board.data ? <span className="ml-1.5 text-tiny text-text-muted">{board.data.counts[p] ?? 0}</span> : null}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-text-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a company" className="h-9 w-56 pl-8" aria-label="Find a company" />
        </div>
        <button type="button" aria-pressed={mine} onClick={() => set('owner', mine ? null : 'me')} className={cn('rounded-full border px-2.5 py-1 text-xs', mine ? 'border-brand bg-accent font-medium' : 'border-border text-muted-foreground hover:bg-accent')}>
          Mine
        </button>
        <button type="button" aria-pressed={closed} onClick={() => set('closed', closed ? null : '1')} className={cn('rounded-full border px-2.5 py-1 text-xs', closed ? 'border-brand bg-accent font-medium' : 'border-border text-muted-foreground hover:bg-accent')}>
          Show won and lost
        </button>
        {!connected ? <span className="text-xs text-muted-foreground">Standalone: not in the forecast, no project.</span> : null}
      </div>

      {board.error ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : board.isPending ? (
        <LoadingState label="Loading the pipeline" />
      ) : (
        <>
          {board.data.forecast ? (
            <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-xl border border-border bg-card px-4 py-2 text-sm shadow-sm">
              <span>
                Open <span className="num font-semibold">{money(board.data.forecast.open)}</span>
              </span>
              <span>
                Weighted <span className="num font-semibold">{money(board.data.forecast.weighted)}</span>
              </span>
              <span className="text-xs text-muted-foreground">Pre-Pilot 15% · Pilot 35% · Reviewing 60% · Contract out 85%</span>
            </div>
          ) : null}
          <div className="grid gap-3 overflow-x-auto pb-2" style={{ gridTemplateColumns: `repeat(${board.data.columns.length}, minmax(15rem, 1fr))` }}>
            {board.data.columns.map((col) => {
              const deals = col.deals.filter(keep)
              return (
                <section key={col.stage} className="grid min-w-0 content-start gap-2 rounded-xl bg-muted/50 p-2" aria-label={col.label}>
                  <header className="px-1" title={col.rule}>
                    <div className="flex items-baseline justify-between gap-2">
                      <h2 className="text-sm font-semibold">{col.label}</h2>
                      <span className="text-xs text-muted-foreground">
                        {deals.length}
                        {pipeline === 'sales' || pipeline === 'post_sales' ? ` · ${money(deals.reduce((n, d) => n + d.value, 0))}` : ''}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-tiny text-text-muted">{col.rule}</p>
                  </header>
                  {deals.map((d) => (
                    <DealCard key={d.id} d={d} owner={name(d.ownerId)} onOpen={() => set('deal', d.id)} />
                  ))}
                  {!deals.length ? <p className="px-1 py-3 text-xs text-text-muted">Nothing here.</p> : null}
                </section>
              )
            })}
          </div>
          {closed && board.data.closed.length ? (
            <section className="mt-4 rounded-xl border border-border bg-card p-3 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold">Won and lost</h2>
              <ul className="grid gap-1 text-sm">
                {board.data.closed.filter(keep).map((d) => (
                  <li key={d.id}>
                    <button type="button" className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-accent" onClick={() => set('deal', d.id)}>
                      <span className={cn('rounded-full px-2 py-0.5 text-tiny font-medium', d.status === 'won' ? 'bg-success-soft text-success' : 'bg-muted text-text-secondary')}>{d.status === 'won' ? 'Won' : 'Lost'}</span>
                      <span className="font-medium">{d.accountName}</span>
                      <span className="text-muted-foreground">{d.lostReason ?? ''}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{shortDay(d.closedAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
      <DealSheet id={openId} onClose={() => set('deal', null)} onOpen={(id) => set('deal', id)} />
      {creating ? <NewDealDialog pipeline={pipeline} onClose={() => setCreating(false)} onCreated={(id) => set('deal', id)} /> : null}
    </Crm2Frame>
  )
}

function DealCard({ d, owner, onOpen }: { d: Deal; owner: string; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="grid min-w-0 gap-1.5 rounded-lg border border-border bg-card p-2.5 text-left shadow-sm hover:border-brand">
      <div className="flex min-w-0 items-start gap-2">
        <Dot level={d.fresh.level} className="mt-1.5" />
        <span className="min-w-0 flex-1 truncate font-medium">{d.accountName}</span>
        {d.value > 0 ? <span className="num text-xs font-semibold">{money(d.value)}</span> : null}
      </div>
      {d.statusName ? <span className={cn('w-fit rounded-full px-2 py-0.5 text-tiny font-semibold', d.stage === 'demo_done' || d.stage === 'contract' ? 'bg-success-soft text-success' : 'bg-info-soft text-info')}>{d.statusName}</span> : null}
      {d.stage === 'nurture' ? (
        <p className="text-xs text-muted-foreground">Check in {shortDay(d.checkInAt)}</p>
      ) : d.nextStep ? (
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {d.nextStep} · {shortDay(d.nextStepAt)}
        </p>
      ) : null}
      {d.fresh.reasons.length ? <p className={cn('w-fit rounded px-1.5 py-0.5 text-tiny', LEVEL_SOFT[d.fresh.level])}>{d.fresh.reasons[0]}</p> : null}
      <div className="flex items-center gap-2 text-tiny text-text-muted">
        <span className="truncate">{owner}</span>
        <span>· {d.fresh.daysInStage}d</span>
        {d.projectId ? (
          <span className="ml-auto inline-flex items-center gap-0.5" title="Open in TapPM">
            <FolderKanban className="size-3" />
            TapPM
          </span>
        ) : null}
      </div>
    </button>
  )
}
